import React, { useState, useEffect } from 'react';
import { signUp, signIn, signInWithGoogle, getUserPostsCount, getUserCommentsCount, getUserReactionsCount } from '../services/authStore';

const RightSidebar = ({ currentUser, profile, referralCode, onShare, onSignOut, onAdminClick, onAuthSuccess, onClose }) => {
  const [expandedWidget, setExpandedWidget] = useState(null);
  const [userStats, setUserStats] = useState({ posts: 0, comments: 0, reactions: 0 });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [authMode, setAuthMode] = useState('signin'); // signin | signup
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [authMessage, setAuthMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      setExpandedWidget('auth');
    }
  }, [currentUser]);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Load user stats when stats widget is expanded or user changes
  useEffect(() => {
    if (!currentUser || expandedWidget !== 'stats') return;
    
    const loadStats = async () => {
      const posts = await getUserPostsCount(currentUser.id);
      const comments = await getUserCommentsCount(currentUser.id);
      const reactions = await getUserReactionsCount(currentUser.id);
      
      setUserStats({ posts, comments, reactions });
    };
    
    loadStats();
  }, [currentUser, expandedWidget]);

  const toggleWidget = (widgetName) => {
    setExpandedWidget(expandedWidget === widgetName ? null : widgetName);
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    const action = authMode === 'signup' ? signUp : signIn;
    const payload = authMode === 'signup' 
      ? { email: authForm.email, password: authForm.password, name: authForm.name }
      : { email: authForm.email, password: authForm.password };
    
    const res = await action(payload);
    if (res.ok) {
      setAuthMessage(authMode === 'signup' ? 'Signed up! Welcome to DevVital.' : 'Signed in successfully.');
      if (authMode === 'signup') {
        setAuthForm({ name: '', email: '', password: '' });
      }
      if (onAuthSuccess) {
        onAuthSuccess();
      }
    } else {
      if (res.code === 429) {
        setAuthMessage('Too many attempts. Please wait a bit before trying again.');
        setCooldown(60);
      } else {
        setAuthMessage(res.error || 'Something went wrong');
      }
    }
  };

  const handleGoogleAuth = () => {
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const redirectTo = `${siteUrl}/`;
    signInWithGoogle(redirectTo).then((res) => {
      if (res.ok && res.url) {
        window.location.href = res.url;
      } else {
        setAuthMessage(res.error || 'Google sign-in failed');
      }
    }).catch(err => {
      setAuthMessage('Google sign-in failed: ' + err.message);
    });
  };

  const getInitials = (name, email) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email ? email[0].toUpperCase() : '?';
  };

  if (isCollapsed) {
    return (
      <div className="w-12 flex-shrink-0 h-full bg-[#0a0a0a] border-l border-white/10 flex flex-col items-center py-4 gap-4 transition-all duration-300">
        <button
          onClick={() => setIsCollapsed(false)}
          className="w-8 h-8 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          title="Expand sidebar"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        {currentUser && (
          <>
            <button
              onClick={() => { setIsCollapsed(false); setExpandedWidget('profile'); }}
              className="w-8 h-8 rounded-full bg-green-btn text-black flex items-center justify-center transition text-xs font-bold"
              title="Profile"
            >
              {getInitials(profile?.name, currentUser.email)}
            </button>
            <button
              onClick={() => { setIsCollapsed(false); setExpandedWidget('share'); }}
              className="w-8 h-8 rounded bg-white/10 hover:bg-green-btn/50 flex items-center justify-center transition text-xs"
              title="Share Referral"
            >
              🔗
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="w-72 flex-shrink-0 h-full bg-[#0a0a0a] border-l border-white/10 overflow-y-auto flex flex-col transition-all duration-300 max-h-[calc(100vh-64px)]">
      {/* Collapse button */}
      <div className="p-3 border-b border-white/10 flex justify-between items-center">
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Info</span>
        <button
          onClick={() => {
            setIsCollapsed(true);
            if (onClose) onClose();
          }}
          className="w-6 h-6 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          title="Collapse sidebar"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {currentUser ? (
        <>
          {/* Profile Widget */}
          <div className="border-b border-white/10">
            <button
              onClick={() => toggleWidget('profile')}
              className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-btn text-black flex items-center justify-center font-bold text-sm">
                  {getInitials(profile?.name, currentUser.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{profile?.name || 'Anonymous'}</div>
                  <div className="text-xs text-gray-500 truncate">{currentUser.email}</div>
                </div>
              </div>
              <svg
                className={`w-4 h-4 transition-transform flex-shrink-0 ${expandedWidget === 'profile' ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {expandedWidget === 'profile' && (
              <div className="p-3 bg-[#0f0f0f] space-y-2">
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Joined</span>
                    <span className="text-white">{new Date(currentUser.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">User ID</span>
                    <span className="text-white font-mono">{currentUser.id.slice(0, 8)}...</span>
                  </div>
                </div>
                <button
                  onClick={onSignOut}
                  className="w-full mt-3 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded transition"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Share Referral Widget */}
          <div className="border-b border-white/10">
            <button
              onClick={() => toggleWidget('share')}
              className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">🔗</span>
                <span className="text-sm font-semibold">Share Referral</span>
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${expandedWidget === 'share' ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {expandedWidget === 'share' && (
              <div className="p-3 bg-[#0f0f0f] space-y-3">
                <p className="text-xs text-gray-400">Your referral link:</p>
                <div className="bg-black border border-white/10 rounded p-2 text-xs break-all text-gray-300">
                  {referralCode ? `${window.location.origin}/?ref=${referralCode}` : 'Generating...'}
                </div>
                <button
                  onClick={onShare}
                  disabled={!referralCode}
                  className="w-full px-3 py-2 bg-green-btn text-black text-xs font-bold rounded hover:opacity-90 disabled:opacity-50"
                >
                  Share Link
                </button>
              </div>
            )}
          </div>

          {/* Admin Widget */}
          {profile?.is_admin && (
            <div className="border-b border-white/10">
              <button
                onClick={() => toggleWidget('admin')}
                className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">⚙️</span>
                  <span className="text-sm font-semibold text-green-btn">Admin</span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform ${expandedWidget === 'admin' ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {expandedWidget === 'admin' && (
                <div className="p-3 bg-[#0f0f0f] space-y-2">
                  <button
                    onClick={onAdminClick}
                    className="w-full px-3 py-2 bg-green-btn text-black text-xs font-bold rounded hover:opacity-90"
                  >
                    Open Admin Panel
                  </button>
                  <p className="text-xs text-gray-500">Moderation & leaderboard</p>
                </div>
              )}
            </div>
          )}

          {/* Stats Widget */}
          <div className="border-b border-white/10">
            <button
              onClick={() => toggleWidget('stats')}
              className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">📊</span>
                <span className="text-sm font-semibold">Your Stats</span>
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${expandedWidget === 'stats' ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {expandedWidget === 'stats' && (
              <div className="p-3 bg-[#0f0f0f] space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">Posts</span>
                  <span className="text-green-btn font-bold">{userStats.posts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Comments</span>
                  <span className="text-green-btn font-bold">{userStats.comments}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Reactions</span>
                  <span className="text-green-btn font-bold">{userStats.reactions}</span>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Auth Widget - Sign In / Sign Up */}
          <div className="border-b border-white/10">
            <button
              onClick={() => toggleWidget('auth')}
              className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">🔐</span>
                <span className="text-sm font-semibold">Sign In / Sign Up</span>
              </div>
              <svg
                className={`w-4 h-4 transition-transform ${expandedWidget === 'auth' ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {expandedWidget === 'auth' && (
              <div className="p-3 bg-[#0f0f0f] space-y-3">
                {/* Mode Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setAuthMode('signin')}
                    className={`flex-1 px-3 py-2 text-xs rounded uppercase tracking-wider transition ${
                      authMode === 'signin'
                        ? 'bg-green-btn text-black font-bold'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setAuthMode('signup')}
                    className={`flex-1 px-3 py-2 text-xs rounded uppercase tracking-wider transition ${
                      authMode === 'signup'
                        ? 'bg-green-btn text-black font-bold'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    Sign Up
                  </button>
                </div>

                {/* Auth Form */}
                <form onSubmit={handleAuthSubmit} className="space-y-2">
                  {authMode === 'signup' && (
                    <input
                      type="text"
                      placeholder="Your name"
                      value={authForm.name}
                      onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                      className="w-full px-3 py-2 bg-black border border-white/10 rounded text-xs text-white focus:outline-none focus:border-green-btn"
                      required
                    />
                  )}
                  <input
                    type="email"
                    placeholder="email@domain.com"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-black border border-white/10 rounded text-xs text-white focus:outline-none focus:border-green-btn"
                    required
                  />
                  <input
                    type="password"
                    placeholder="password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                    className="w-full px-3 py-2 bg-black border border-white/10 rounded text-xs text-white focus:outline-none focus:border-green-btn"
                    required
                  />
                  <button
                    type="submit"
                    disabled={cooldown > 0}
                    className="w-full px-3 py-2 bg-green-btn text-black text-xs font-bold rounded hover:opacity-90 disabled:opacity-50"
                  >
                    {cooldown > 0 ? `Retry in ${cooldown}s` : (authMode === 'signup' ? 'Create Account' : 'Sign In')}
                  </button>
                </form>

                {/* Google OAuth */}
                <button
                  onClick={handleGoogleAuth}
                  className="w-full px-3 py-2 bg-white text-black text-xs font-semibold rounded hover:opacity-90 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>

                {authMessage && (
                  <p className={`text-xs ${authMessage.includes('success') || authMessage.includes('Welcome') ? 'text-green-400' : 'text-red-400'}`}>
                    {authMessage}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Info Widget for non-logged users */}
          <div className="p-4 text-center space-y-3">
            <p className="text-sm text-gray-400">Join DevVital to:</p>
            <ul className="text-xs text-gray-500 space-y-1 text-left">
              <li>✓ Share health tips & experiences</li>
              <li>✓ Get referral rewards</li>
              <li>✓ Join exclusive discussions</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default RightSidebar;
